import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, X } from "lucide-react";
import { AIDescriptionAssistant } from "../components/AIDescriptionAssistant";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const CATEGORIES = ["Anniversaire", "Mariage", "Solidarité", "Études", "Voyage", "Projet", "Famille", "Sport", "Autre"];

export default function EditCagnotte() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get(`/cagnottes/${id}`).then(({ data }) => {
      setForm({
        title: data.title,
        description: data.description,
        goal_amount: data.goal_amount || "",
        end_date: data.end_date ? data.end_date.split("T")[0] : "",
        visibility: data.visibility,
        category: data.category || "",
        image_url: data.image_url || "",
      });
      if (data.image_url) setImagePreview(fileUrl(data.image_url));
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd);
      set("image_url", data.path);
      setImagePreview(URL.createObjectURL(file));
    } catch (err) {
      toast.error("Échec");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        goal_amount: form.goal_amount ? parseFloat(form.goal_amount) : null,
        end_date: form.end_date || null,
        category: form.category || null,
        image_url: form.image_url || null,
      };
      await api.patch(`/cagnottes/${id}`, payload);
      toast.success("Mise à jour réussie");
      navigate(`/cagnotte/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/cagnottes/${id}`);
      toast.success("Cagnotte supprimée");
      navigate("/dashboard");
    } catch {
      toast.error("Erreur");
    }
  };

  if (!form) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="edit-cagnotte-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl font-bold tracking-tight">Modifier</h1>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-full text-destructive border-destructive/30" data-testid="delete-cagnotte-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Supprimer
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette cagnotte ?</AlertDialogTitle>
              <AlertDialogDescription>Cette action est irréversible. Toutes les contributions seront perdues.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="bg-destructive" data-testid="confirm-delete">Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <form onSubmit={submit} className="card-soft p-8 space-y-6">
        <div>
          <Label>Image de couverture</Label>
          <div className="mt-2">
            {imagePreview ? (
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-secondary">
                <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => { setImagePreview(null); set("image_url", ""); }} className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="aspect-[16/9] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-primary text-muted-foreground hover:text-foreground transition">
                {uploading ? <Loader2 className="h-8 w-8 animate-spin" /> : <><Upload className="h-8 w-8 mb-2" /><span className="text-sm">Téléverser</span></>}
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="title">Titre</Label>
          <Input id="title" required value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label htmlFor="description">Description</Label>
            <AIDescriptionAssistant kind="cagnotte" value={form.description} onChange={(t) => set("description", t)} context={{ title: form.title, goal_amount: form.goal_amount, end_date: form.end_date, category: form.category, currency: "XOF" }} />
          </div>
          <Textarea id="description" required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-xl" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="goal">Objectif (FCFA)</Label>
            <Input id="goal" type="number" min="0" value={form.goal_amount} onChange={(e) => set("goal_amount", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="end_date">Date de fin</Label>
            <Input id="end_date" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Visibilité</Label>
            <Select value={form.visibility} onValueChange={(v) => set("visibility", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Publique</SelectItem>
                <SelectItem value="private">Privée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-full">Annuler</Button>
          <Button type="submit" disabled={loading} className="btn-gradient rounded-full flex-1 h-11" data-testid="submit-edit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
